import React, {
  useContext,
  SyntheticEvent,
  useState,
  ChangeEvent,
  useEffect,
} from "react";
import URI from "urijs";
import { AppPage } from "../../components";
import { ChoiceList, Choice } from "@cmsgov/ds-medicare-gov";
import { ActionType, PrescriptionDrug } from "../../@types";
import {
  displayDrug,
  extractPDEsFromBene,
  useTranslate,
  sendTealiumEvent,
} from "../../helpers";
import { Routes } from "../../app";
import { AppContext } from "../../app/store";
import { RouteComponentProps } from "react-router";
import { setPlanTypeFromURL } from "../../app/modules/location/actions";
import { Link } from "react-router-dom";

const PDEPage: React.FunctionComponent<RouteComponentProps> = ({
  history,
  location,
}) => {
  const { state, dispatch } = useContext(AppContext);
  const [choices, setChoices] = useState<
    { label: string; value: string; checked?: boolean }[]
  >([]);
  const [savedPdes, setSavedPdes] = useState<PrescriptionDrug[]>([]);
  const [unsavedPdes, setUnsavedPdes] = useState<PrescriptionDrug[]>([]);
  const t = useTranslate();
  const isStandaloneDrugPage = state.mbpReferrerUri !== "";

  useEffect(() => {
    if (!state.planType) {
      const url = new URI(location.search);
      setPlanTypeFromURL(dispatch, url);
    }

    if (state.beneficiary) {
      const pdes = extractPDEsFromBene(state.beneficiary);
      const saved: PrescriptionDrug[] = [];
      const unsaved: PrescriptionDrug[] = [];

      pdes.forEach(p => {
        if (!state.prescriptions.find(d => d.ndc === p.ndc)) {
          unsaved.push(p);
        } else {
          saved.push(p);
        }
      });

      setUnsavedPdes(unsaved);
      setChoices(
        unsaved.map(prescription => {
          const label = displayDrug(t, prescription);

          return {
            label,
            value: prescription.ndc,
            checked: false,
          };
        })
      );
      setSavedPdes(saved);
    }
  }, []);

  if (!state.beneficiary) {
    history.replace(`${Routes.managePrescriptions}${location.search}`);
  }

  const selectAll = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.currentTarget.checked) {
      sendTealiumEvent("Find Plans - LIS Select Drugs", "Select all drugs");
    }
    setChoices(
      choices.map(choice => {
        return { ...choice, checked: event.target.checked };
      })
    );
  };

  const handleSelection = (e: SyntheticEvent<HTMLInputElement>): void => {
    if (e.currentTarget.checked) {
      // istanbul ignore next: tested elsewhere
      sendTealiumEvent("Find Plans - LIS Select Drugs", "Drug Selected");
    }
    const ndc = e.currentTarget.value;

    setChoices(
      choices.map(choice => {
        if (choice.value === ndc) {
          return { ...choice, checked: e.currentTarget.checked };
        }
        return choice;
      })
    );
  };

  const sendItOut = (): void => {
    const selectedPdes = unsavedPdes.filter(pde => {
      return choices.find(choice => choice.checked && choice.value === pde.ndc);
    });

    dispatch({
      type: ActionType.SET_PRESCRIPTIONS,
      payload: [...state.prescriptions, ...selectedPdes],
    });

    const tealiumDrugList = selectedPdes.map(d => d.name);
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent(
      "Find Plans - LIS Confirm Drugs",
      tealiumDrugList.join("|")
    );
  };

  const allSelected = choices
    .map(choice => choice.checked)
    .every(drugIncluded => drugIncluded);

  return (
    <AppPage heading={t("pde_page.add_recently_filled_drugs")}>
      <div className="PDEPage">
        <p className="ds-u-measure--wide ds-u-margin-bottom--3 ds-u-margin-top--0">
          {t("pde_page.pde_information")}
        </p>
        {!!savedPdes.length && (
          <div className="ds-u-margin-top--1 ds-u-margin-bottom--4">
            {t("pde_page.drugs_already_saved")}
            {/* the redundant roles are a fix for a safari VO bug */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ul className="ds-c-list ds-c-list--bare saved-pdes" role="list">
              {savedPdes.map(pde => {
                return (
                  // eslint-disable-next-line jsx-a11y/no-redundant-roles
                  <li key={pde.ndc} role="listitem">
                    {displayDrug(t, pde)}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {!!choices.length && (
          <ChoiceList
            label={<span id="add_drugs">{t("pde_page.add_these_drugs")}</span>}
            hint={
              <Choice
                type="checkbox"
                className="ds-u-border-bottom--1 ds-u-padding-bottom--1 ds-u-margin-bottom--1 ds-u-measure--narrow"
                onChange={selectAll}
                checked={allSelected}
                value="select-all"
                name="select-all-pdes"
              >
                {t("pde_page.select_all_drugs")}
              </Choice>
            }
            choices={choices}
            className="ds-u-margin-top--2 unsaved-pdes"
            name="pdeChoices"
            onChange={handleSelection}
            type="checkbox"
          />
        )}
        <section className="ContinueSection">
          <Link
            to={
              state.prescriptions.length ||
              choices.some(choice => choice.checked)
                ? `${Routes.prescriptionsList}${location.search}`
                : `${Routes.managePrescriptions}${location.search}`
            }
            className="ds-c-button ds-c-button--primary e2e-continue-button mct-u-width--full mct-u-sm-width--auto"
            onClick={sendItOut}
            data-testid="pde-next-button"
          >
            {isStandaloneDrugPage
              ? t("manage_prescriptions.back_to_my_saved_drugs")
              : t("landing_page.continue")}
          </Link>
        </section>
      </div>
    </AppPage>
  );
};

export default PDEPage;
